<script setup>
/* Aktionsleiste des Markier-Modus. Handy: fest am unteren Rand (über dem Home-Balken), Laptop: oben,
   mitlaufend. Steht in SquadPager außerhalb der verschobenen Seiten, weil ein transformiertes Elternelement
   position: fixed an sich binden würde. Zähler, Hinweis, Aktionen, Select all / Clear / Done.
   Aktionen nach Rolle: "Create list" nur mit Exportrecht (canExportPlayerList: admin, board, data, staff) — nicht
   ausgegraut, sondern gar nicht da; "Copy as text" für alle mit Markier-Modus. */
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { mdiDotsHorizontal, mdiCheck, mdiFormatListBulleted, mdiContentCopy, mdiShareVariantOutline, mdiTagOutline } from '@mdi/js';
import OverlayModal from './OverlayModal.vue';
import ExportPlayerListDialog from './ExportPlayerListDialog.vue';
import ShareStatus from './ShareStatus.vue';
import { usePlayers } from '../composables/usePlayers.js';
import { useSelection } from '../composables/useSelection.js';
import { useAuth } from '../composables/useAuth.js';
import { useSnackbar } from '../composables/useSnackbar.js';
import { useConfirm } from '../composables/useConfirm.js';
import { AGE_GROUPS } from '../domain/ageGroups.js';
import { useShareFiles } from '../composables/useShareFiles.js';
import { useProfilePdf } from '../composables/useProfilePdf.js';
import { flattenSections } from '../domain/profileNav.js';
import { canExportPlayerList } from '../domain/playerListExport.js';
import { buildPlayerText } from '../domain/playerText.js';
import { exportLabels } from '../export/exportLabels.js';

const { t } = useI18n();
const { role, can } = useAuth();
const { confirm } = useConfirm();
const { show } = useSnackbar();
const { players, squadSections, setAgeGroup } = usePlayers();
const { selecting, ids, resolve, selectAll, clear, done } = useSelection();
const share = useShareFiles();

/* Markierte Spieler in der Reihenfolge der angezeigten Liste (Abschnitte nacheinander); Markierte, die der
   Filter gerade ausblendet, stehen dahinter. So folgen Profil-PDF-Seiten und Dateinamen der Liste. */
const selected = computed(() => {
  const order = new Map(flattenSections(squadSections.value).map((id, i) => [id, i]));
  const rank = (p) => (order.has(p.id) ? order.get(p.id) : Number.MAX_SAFE_INTEGER);
  return resolve(players.value).slice().sort((a, b) => rank(a) - rank(b));
});
const count = computed(() => selected.value.length);
void ids; // Abhängigkeit: Zähler folgt der Markierung

const menuOpen = ref(false);
const canCreateList = computed(() => canExportPlayerList(role?.value));
/* Altersklasse für alle Markierten setzen: nur mit Schreibrecht auf Spieler (admin, board, data) — nicht ausgegraut,
   sondern gar nicht da für staff und guest. Die Datenbank erzwingt es zusätzlich (players_update). */
const canSetAgeGroup = computed(() => can('players', 'write'));
const ageOpen = ref(false);
const ageBusy = ref(false);

/** value: 'U17' | 'U19' | '' (None). Erst Rückfrage, dann schreiben, dann Rückmeldung mit der Zahl der geänderten Spieler. */
async function onSetAgeGroup(value) {
  ageOpen.value = false;
  const total = count.value;
  if (!canSetAgeGroup.value || !total || ageBusy.value) return;
  const message = value
    ? t('ageGroup.confirmMessage', { count: total, value }, total)
    : t('ageGroup.confirmNone', { count: total }, total);
  const ok = await confirm({ title: t('ageGroup.confirmTitle'), message, confirmLabel: t('ageGroup.apply'), danger: false });
  if (!ok) return;
  ageBusy.value = true;
  try {
    const { changed, failed } = await setAgeGroup(selected.value.map((p) => p.id), value);
    if (failed) show(t('ageGroup.partial', { changed, total, failed }), 'warning');
    else show(t(value ? 'ageGroup.done' : 'ageGroup.cleared', { count: changed }, changed), 'success');
  } catch {
    show(t('ageGroup.failed'), 'error');
  } finally {
    ageBusy.value = false;
  }
}

/* ---------- Create list: der vorhandene Export-Dialog mit dem Umfang "selection" ---------- */
const listOpen = ref(false);
function onCreateList() {
  /* Doppelt geprüft: die Taste fehlt ohne Recht, und usePlayerListExport.run verweigert ebenfalls */
  if (canCreateList.value && count.value) listOpen.value = true;
}

/* ---------- Share > Player card (PDF): wird aus Player Info und Material erzeugt ----------
   Es gibt keine hochgeladene Spielerkarte mehr; jeder markierte Spieler bekommt seine erzeugte Karte. */
const shareOpen = ref(false);

/** Ergebnis des Teilens als Satz; leer bei Abbruch (cancelled, dismissed, busy) und beim Notknopf (needsTap). */
function shareMessage(out) {
  if (out.status === 'shared') return t('selection.sharedFiles', { count: out.count }, out.count);
  if (out.status === 'downloaded') {
    return `${t('selection.downloadedFiles', { count: out.count }, out.count)} ${t('selection.downloadHint')}`;
  }
  return '';
}

const profileMode = ref('one'); // 'one' = ein PDF, eine Seite je Spieler · 'separate' = je Spieler eine Datei
const profilePdf = useProfilePdf();

async function onSharePlayerCards() {
  shareOpen.value = false;
  if (!count.value) return;
  const out = await share.runWith(profilePdf.producerFor(selected.value, { mode: profileMode.value }));
  if (out.status === 'cancelled' || out.status === 'busy') return;
  const problems = [];
  if (out.status === 'none') problems.push(t('selection.shareFailed'));
  const text = [shareMessage(out), ...problems].filter(Boolean).join(' ');
  if (text) show(text, problems.length ? 'warning' : 'success');
}

async function onShareNow() {
  const text = shareMessage(await share.shareNow());
  if (text) show(text);
}

/* ---------- Copy as text ---------- */
const fallbackText = ref('');
const fallbackOpen = ref(false);

async function onCopyText() {
  if (!count.value) return;
  const text = buildPlayerText(selected.value, exportLabels(t, { table: { scope: 'selection', total: count.value } }));
  try {
    if (!navigator.clipboard?.writeText) throw new Error('noClipboard');
    await navigator.clipboard.writeText(text);
    show(t('selection.copied', { count: count.value }, count.value));
  } catch {
    /* Nie still scheitern: der Text steht zum Markieren und Kopieren im Dialogfeld */
    fallbackText.value = text;
    fallbackOpen.value = true;
  }
}

/* "Select all" = alles, was nach Filter, Suche und Jahrgang gerade sichtbar ist */
function onSelectAll() {
  selectAll(flattenSections(squadSections.value));
  menuOpen.value = false;
}
function onClear() {
  clear();
  menuOpen.value = false;
}

function onKey(e) {
  if (e.key !== 'Escape') return;
  if (menuOpen.value || shareOpen.value || ageOpen.value) {
    menuOpen.value = false;
    shareOpen.value = false;
    ageOpen.value = false;
  } else done();
}
/* Esc beendet den Modus — der Listener hängt nur, solange der Modus an ist */
function bindKeys(on) {
  menuOpen.value = false;
  shareOpen.value = false;
  ageOpen.value = false;
  if (typeof document === 'undefined') return;
  if (on) document.addEventListener('keydown', onKey);
  else document.removeEventListener?.('keydown', onKey);
}
watch(selecting, bindKeys);
onMounted(() => {
  if (selecting.value) bindKeys(true);
});

/* Tippen oder Klicken außerhalb eines offenen Menüs schließt es. Der Listener hängt nur, solange ein Menü
   offen ist, und geht beim Schließen, beim Beenden des Modus und beim Abbau der Leiste wieder weg. */
function onOutside(e) {
  const target = e && e.target;
  if (target && typeof target.closest === 'function' && target.closest('.sel-more')) return;
  menuOpen.value = false;
  shareOpen.value = false;
  ageOpen.value = false;
}
function bindOutside(on) {
  if (typeof document === 'undefined') return;
  if (on) document.addEventListener('pointerdown', onOutside);
  else document.removeEventListener?.('pointerdown', onOutside);
}
watch(() => menuOpen.value || shareOpen.value || ageOpen.value, bindOutside);
onBeforeUnmount(() => {
  bindKeys(false);
  bindOutside(false);
});
</script>

<template>
  <div v-if="selecting" class="sel-bar" role="toolbar" :aria-label="$t('selection.toolbar')" data-testid="selection-bar">
    <span class="sel-count" aria-live="polite" data-testid="selection-count">
      {{ count ? $t('selection.count', { count }) : $t('selection.hint') }}
    </span>

    <div class="sel-actions">
      <slot :selected="selected" />

      <template v-if="count">
        <div class="sel-more">
          <button
            type="button"
            class="btn ghost sel-btn"
            :aria-label="$t('selection.share')"
            :title="$t('selection.share')"
            aria-haspopup="true"
            :aria-expanded="shareOpen"
            data-testid="selection-share"
            @click="shareOpen = !shareOpen; menuOpen = false; ageOpen = false"
          >
            <v-icon :icon="mdiShareVariantOutline" size="16" />
            <span class="sel-text">{{ $t('selection.share') }}</span>
          </button>
          <div v-if="shareOpen" class="sel-menu" role="menu">
            <button type="button" class="sel-item" role="menuitem" data-testid="share-player-card" @click="onSharePlayerCards">
              {{ $t('selection.sharePlayerCard') }}
            </button>
            <!-- Bei mehreren Spielern: ein PDF mit einer Seite je Spieler (Vorgabe) oder getrennte Dateien -->
            <template v-if="count > 1">
              <button
                type="button"
                class="sel-item sel-radio"
                role="menuitemradio"
                :aria-checked="profileMode === 'one'"
                data-testid="profile-mode-one"
                @click="profileMode = 'one'"
              >
                <span aria-hidden="true">{{ profileMode === 'one' ? '●' : '○' }}</span>
                {{ $t('selection.profileOne') }}
              </button>
              <button
                type="button"
                class="sel-item sel-radio"
                role="menuitemradio"
                :aria-checked="profileMode === 'separate'"
                data-testid="profile-mode-separate"
                @click="profileMode = 'separate'"
              >
                <span aria-hidden="true">{{ profileMode === 'separate' ? '●' : '○' }}</span>
                {{ $t('selection.profileSeparate') }}
              </button>
            </template>
          </div>
        </div>
        <button
          v-if="canCreateList"
          type="button"
          class="btn ghost sel-btn"
          :aria-label="$t('selection.createList')"
          :title="$t('selection.createList')"
          data-testid="selection-create-list"
          @click="onCreateList"
        >
          <v-icon :icon="mdiFormatListBulleted" size="16" />
          <span class="sel-text">{{ $t('selection.createList') }}</span>
        </button>
        <div v-if="canSetAgeGroup" class="sel-more">
          <button
            type="button"
            class="btn ghost sel-btn"
            :aria-label="$t('ageGroup.setAction')"
            :title="$t('ageGroup.setAction')"
            aria-haspopup="true"
            :aria-expanded="ageOpen"
            :disabled="ageBusy"
            data-testid="selection-age-group"
            @click="ageOpen = !ageOpen; menuOpen = false; shareOpen = false"
          >
            <v-icon :icon="mdiTagOutline" size="16" />
            <span class="sel-text">{{ $t('ageGroup.setAction') }}</span>
          </button>
          <div v-if="ageOpen" class="sel-menu" role="menu">
            <button
              v-for="g in AGE_GROUPS"
              :key="g"
              type="button"
              class="sel-item"
              role="menuitem"
              :data-testid="`age-group-set-${g}`"
              @click="onSetAgeGroup(g)"
            >
              {{ g }}
            </button>
            <button type="button" class="sel-item" role="menuitem" data-testid="age-group-set-none" @click="onSetAgeGroup('')">
              {{ $t('ageGroup.none') }}
            </button>
          </div>
        </div>
        <button
          type="button"
          class="btn ghost sel-btn"
          :aria-label="$t('selection.copyText')"
          :title="$t('selection.copyText')"
          data-testid="selection-copy-text"
          @click="onCopyText"
        >
          <v-icon :icon="mdiContentCopy" size="16" />
          <span class="sel-text">{{ $t('selection.copyText') }}</span>
        </button>
      </template>

      <div class="sel-more">
        <button
          type="button"
          class="btn ghost sel-btn sel-icon"
          :aria-label="$t('selection.more')"
          :title="$t('selection.more')"
          aria-haspopup="true"
          :aria-expanded="menuOpen"
          data-testid="selection-more"
          @click="menuOpen = !menuOpen; shareOpen = false; ageOpen = false"
        >
          <v-icon :icon="mdiDotsHorizontal" size="18" />
        </button>
        <div v-if="menuOpen" class="sel-menu" role="menu">
          <button type="button" class="sel-item" role="menuitem" data-testid="selection-all" @click="onSelectAll">
            {{ $t('selection.selectAll') }}
          </button>
          <button type="button" class="sel-item" role="menuitem" :disabled="!count" data-testid="selection-clear" @click="onClear">
            {{ $t('selection.clear') }}
          </button>
        </div>
      </div>

      <!-- "Done" ist immer direkt erreichbar, nicht nur im Menü -->
      <button type="button" class="btn sel-btn" data-testid="selection-done" @click="done">
        <v-icon :icon="mdiCheck" size="16" />
        <span>{{ $t('selection.done') }}</span>
      </button>
    </div>

    <!-- Fortschritt "Loading 3 of 12" mit Cancel; Notknopf nach abgelaufener Berührung -->
    <ShareStatus @share-now="onShareNow" />
  </div>

  <ExportPlayerListDialog v-if="canCreateList" v-model="listOpen" selection />

  <OverlayModal v-if="fallbackOpen" :title="$t('selection.copyFallbackTitle')" :width="460" @close="fallbackOpen = false">
    <p class="sel-fallback-hint">{{ $t('selection.copyFallbackHint') }}</p>
    <textarea class="sel-fallback" readonly rows="10" :value="fallbackText" data-testid="selection-fallback" @focus="$event.target.select()"></textarea>
    <template #actions>
      <button class="btn" @click="fallbackOpen = false">{{ $t('common.close') }}</button>
    </template>
  </OverlayModal>
</template>

<style scoped>
.sel-bar {
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  box-sizing: border-box;
  padding: 8px 12px;
  background: var(--panel);
  border: 1px solid var(--border);
  /* Handy: unten, über dem Home-Balken */
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
  border-width: 1px 0 0;
}
@media (min-width: 1024px) {
  /* Laptop: oben, läuft beim Scrollen mit */
  .sel-bar {
    position: sticky;
    left: auto;
    right: auto;
    bottom: auto;
    top: 0;
    padding-bottom: 8px;
    border-width: 0 0 1px;
  }
}
.sel-count {
  font-size: 13px;
  font-weight: 500;
  color: var(--text);
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sel-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: none;
}
.sel-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: 40px;
  min-width: 40px;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 500;
}
/* Schmales Handy (360 px): nur das Symbol, der Name steht im aria-label */
@media (max-width: 480px) {
  .sel-text {
    display: none;
  }
}
/* Handy: das Menü hängt an der Leiste (die ist fixed), nicht am Knopf — so läuft es nie aus dem Bild */
.sel-more {
  position: static;
}
@media (min-width: 1024px) {
  .sel-more {
    position: static;
  }
}
.sel-fallback-hint {
  margin: 0 0 10px;
  font-size: 0.85rem;
  color: var(--muted);
}
.sel-fallback {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  font: inherit;
  font-size: 13px;
  color: var(--text);
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 9px;
  resize: vertical;
}
.sel-menu {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: calc(100% + 6px);
  max-width: calc(100vw - 24px);
  display: flex;
  flex-direction: column;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 9px;
  overflow: hidden;
}
@media (min-width: 1024px) {
  /* Laptop: Leiste oben, Menü rechtsbündig darunter */
  .sel-menu {
    left: auto;
    right: 12px;
    bottom: auto;
    top: calc(100% + 6px);
    width: max-content;
    max-width: min(360px, calc(100vw - 24px));
  }
}
.sel-item {
  min-height: 44px;
  padding: 8px 14px;
  white-space: normal;
  text-align: left;
  font-size: 13px;
  font-weight: 500;
  color: var(--text);
  background: transparent;
  border: 0;
  cursor: pointer;
}
.sel-radio {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: normal;
  font-weight: 400;
  color: var(--muted);
}
.sel-item:hover:not(:disabled) {
  background: var(--card-hover);
}
.sel-item:disabled {
  color: var(--dim);
  cursor: default;
}
</style>
